/*
 * RolePreview — single reusable component for all 260 role detail pages.
 * UI: EXACT AI Pathway design system (mirrors AIPathwayPreview.jsx) — white
 * rounded-3xl cards with thin colored borders + gradient icon circles,
 * colored sub-panels with small-caps labels, ">" arrow bullets, chips.
 * Every section has a purpose-built renderer with a safe MarkdownBody
 * fallback when the markdown shape doesn't match.
 * DATA: LearnixTa_260_Roles_FINAL — role.sections_md (16 md sections) +
 * role.quiz (16Q, pass 11/16) rendered after the last section.
 *
 * Do NOT expose: quiz.answerIndex, raw JSON, debug data.
 */
import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Flame, Compass, Layers, GraduationCap, Bot,
  Building2, ShieldAlert, MapPinned, Wrench, Footprints, Trophy, Briefcase,
  HelpCircle, Brain, MessageCircle, Lightbulb, Activity,
  CheckCircle2, XCircle, RotateCcw, ChevronRight, Copy, Check,
} from 'lucide-react';
import { RoadmapLayerEntry } from './RoadmapLayer';

/* Pill-tab labels (order matches the 22-section sequence; quiz last) */
export const PREVIEW_SECTIONS = [
  { id: 'tldr',          label: 'Key Takeaways',   icon: Flame },
  { id: 'snapshot',      label: 'Snapshot',        icon: Activity },
  { id: 'role-displacement-outlook', label: 'Prediction', icon: Activity },
  { id: 'big-picture',   label: 'Big Picture',     icon: Compass },
  { id: 'what',          label: 'The Role',        icon: Layers },
  { id: 'eligibility',   label: 'Eligibility',     icon: GraduationCap },
  { id: 'roadmap',       label: 'Roadmap',         icon: MapPinned },
  { id: 'skills',        label: 'Skill Stack',     icon: Wrench },
  { id: 'learning',      label: 'Learning Path',   icon: Footprints },
  { id: 'operating',     label: 'You + AI',        icon: Bot },
  { id: 'integration',   label: 'Real Company',    icon: Building2 },
  { id: 'risk-map',      label: 'Risk Map',        icon: ShieldAlert },
  { id: 'proof',         label: 'Build Proof',     icon: Trophy },
  { id: 'hiring',        label: 'Getting Hired',   icon: Briefcase },
  { id: 'myth',          label: 'Myths',           icon: HelpCircle },
  { id: 'prompts',       label: 'Prompt Pack',     icon: MessageCircle },
  { id: 'one-idea',      label: 'One Idea',        icon: Lightbulb },
  { id: 'quiz',          label: 'Quiz',            icon: Brain },
];

const SECTION_ACCENT = {
  tldr: 'amber', snapshot: 'teal', 'role-displacement-outlook': 'amber', 'big-picture': 'indigo', what: 'purple',
  eligibility: 'emerald', operating: 'cyan', integration: 'blue',
  'risk-map': 'rose', roadmap: 'indigo', skills: 'teal', learning: 'indigo',
  proof: 'amber', hiring: 'blue', myth: 'purple', quiz: 'purple',
  prompts: 'cyan', 'one-idea': 'amber',
};

/* Same ACCENTS map as AIPathwayPreview.jsx */
const ACCENTS = {
  teal:    { card: 'border-teal-200 bg-teal-50/40',       icon: 'from-teal-400 to-emerald-500' },
  amber:   { card: 'border-amber-200 bg-amber-50/40',     icon: 'from-amber-400 to-orange-500' },
  rose:    { card: 'border-rose-200 bg-rose-50/40',       icon: 'from-rose-400 to-pink-500' },
  indigo:  { card: 'border-indigo-200 bg-indigo-50/40',   icon: 'from-indigo-400 to-violet-500' },
  emerald: { card: 'border-emerald-200 bg-emerald-50/40', icon: 'from-emerald-400 to-teal-500' },
  purple:  { card: 'border-purple-200 bg-purple-50/40',   icon: 'from-purple-400 to-fuchsia-500' },
  cyan:    { card: 'border-cyan-200 bg-cyan-50/40',       icon: 'from-cyan-400 to-sky-500' },
  blue:    { card: 'border-blue-200 bg-blue-50/40',       icon: 'from-blue-400 to-indigo-500' },
};

const stripEmoji = (s) => String(s || '').replace(/^[^A-Za-z0-9"'(]+/, '').trim();

/* EXACT copy of AIPathwayPreview.jsx Section chrome */
const Section = ({ id, icon: Icon, title, accent = 'teal', children }) => {
  const pal = ACCENTS[accent] || ACCENTS.teal;
  return (
    <section
      id={id}
      data-testid={`fr-section-${id}`}
      className={`bg-white border-2 ${pal.card} rounded-3xl p-5 md:p-7 shadow-sm scroll-mt-52`}
    >
      <div className="flex items-center gap-3 mb-4">
        <span className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${pal.icon} text-white flex items-center justify-center shrink-0 shadow-sm`}>
          <Icon size={18} />
        </span>
        <h2 className="text-lg md:text-xl font-extrabold text-gray-900">{title}</h2>
      </div>
      <div className="text-gray-700 text-base leading-relaxed">{children}</div>
    </section>
  );
};

/* Same InfoCard as AIPathwayPreview.jsx — colored sub-panel + small-caps label */
const Panel = ({ label, children, tone = 'teal', className = '' }) => {
  const tones = {
    teal:    'bg-teal-50 border-teal-200',
    sky:     'bg-sky-50 border-sky-200',
    mint:    'bg-emerald-50 border-emerald-200',
    cream:   'bg-amber-50 border-amber-200',
    violet:  'bg-violet-50 border-violet-200',
    rose:    'bg-rose-50 border-rose-200',
    blue:    'bg-blue-50 border-blue-200',
  };
  const labelTones = {
    teal: 'text-teal-800', sky: 'text-sky-800', mint: 'text-emerald-800',
    cream: 'text-amber-800', violet: 'text-violet-800', rose: 'text-rose-800',
    blue: 'text-blue-800',
  };
  return (
    <div className={`rounded-2xl border ${tones[tone] || tones.teal} p-4 md:p-5 ${className}`}>
      {label && <p className={`text-[12.5px] uppercase font-bold tracking-[0.18em] mb-1.5 ${labelTones[tone] || labelTones.teal}`}>{label}</p>}
      <div className="text-[17px] text-gray-800 leading-relaxed">{children}</div>
    </div>
  );
};

/* Same ">" arrow bullets as AIPathwayPreview.jsx */
const Bullets = ({ items, tone = 'teal' }) => {
  const tones = {
    teal: 'bg-teal-100 text-teal-700',
    amber: 'bg-amber-100 text-amber-700',
    blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
    rose: 'bg-rose-100 text-rose-700',
    emerald: 'bg-emerald-100 text-emerald-700',
  };
  return (
    <ul className="space-y-2">
      {(items || []).map((x, i) => (
        <li key={i} className="flex gap-2.5 text-[17px] leading-relaxed">
          <span className={`w-5 h-5 rounded-full ${tones[tone]} flex items-center justify-center mt-0.5 shrink-0`}>
            <ChevronRight size={12} />
          </span>
          <span className="min-w-0 break-words [overflow-wrap:anywhere]"><InlineMD md={String(x)} /></span>
        </li>
      ))}
    </ul>
  );
};

const Meter = ({ label, valueRaw, grad = 'from-teal-400 to-emerald-500' }) => {
  const m = typeof valueRaw === 'string' ? valueRaw.match(/(\d+(?:\.\d+)?)\s*\/\s*(\d+)/) : null;
  const value = m ? parseFloat(m[1]) : null;
  const max = m ? (parseFloat(m[2]) || 5) : 5;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span className="text-[15px] font-bold text-gray-900 min-w-0 break-words">{label}</span>
        <span className="text-[15px] font-extrabold text-gray-900 shrink-0">{valueRaw}</span>
      </div>
      <div className="flex gap-1.5">
        {Array.from({ length: max }).map((_, i) => (
          <div
            key={i}
            className={`h-2 flex-1 rounded-full ${
              value !== null && i < Math.round(value) ? `bg-gradient-to-r ${grad}` : 'bg-gray-200'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const Chip = ({ children, tone = 'teal' }) => {
  const tones = {
    teal:   'bg-teal-100 text-teal-800 border-teal-200',
    slate:  'bg-slate-100 text-slate-700 border-slate-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[13px] font-semibold max-w-full break-words [overflow-wrap:anywhere] ${tones[tone]}`}>
      {children}
    </span>
  );
};

/* Markdown — AI Pathway typography; teal links + teal table headers */
const MD_COMPONENTS = {
  h1: ({ children }) => <p className="text-[12.5px] uppercase font-bold tracking-widest text-gray-600 mt-5 mb-2">{children}</p>,
  h2: ({ children }) => <p className="text-[12.5px] uppercase font-bold tracking-widest text-gray-600 mt-5 mb-2">{children}</p>,
  h3: ({ children }) => <p className="text-[12.5px] uppercase font-bold tracking-widest text-gray-600 mt-4 mb-1.5">{children}</p>,
  h4: ({ children }) => <p className="text-[12.5px] uppercase font-bold tracking-widest text-gray-600 mt-3 mb-1">{children}</p>,
  p:  ({ children }) => <p className="text-[17px] text-gray-800 leading-relaxed my-2.5 break-words [overflow-wrap:anywhere]">{children}</p>,
  strong: ({ children }) => <strong className="font-bold text-gray-900">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="space-y-2 my-2.5 list-disc pl-5 marker:text-teal-500">{children}</ul>,
  ol: ({ children }) => <ol className="space-y-2 my-2.5 list-decimal pl-5 marker:font-bold marker:text-gray-500">{children}</ol>,
  li: ({ children }) => (
    <li className="text-[17px] text-gray-800 leading-relaxed break-words [overflow-wrap:anywhere]">{children}</li>
  ),
  blockquote: ({ children }) => (
    <div className="rounded-2xl border bg-amber-50 border-amber-200 p-4 md:p-5 my-3 text-[17px] text-gray-800 leading-relaxed break-words [overflow-wrap:anywhere] [&>p]:my-0">
      {children}
    </div>
  ),
  hr: () => <hr className="my-4 border-gray-200" />,
  pre: ({ children }) => (
    <pre className="bg-white border border-gray-200 rounded-2xl p-4 my-3 overflow-x-auto whitespace-pre-wrap break-words text-[14px] leading-relaxed [&_code]:bg-transparent [&_code]:p-0">{children}</pre>
  ),
  code: ({ children }) => <code className="bg-slate-100 text-slate-800 rounded px-1.5 py-0.5 text-[14px] break-words">{children}</code>,
  table: ({ children }) => (
    <div className="my-3 rounded-2xl border border-gray-200 overflow-hidden overflow-x-auto">
      <table className="w-full text-[15px] min-w-[480px]">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead>{children}</thead>,
  tr: ({ children }) => <tr className="border-t border-gray-100 first:border-t-0 [thead_&]:bg-teal-50 [thead_&]:text-teal-900">{children}</tr>,
  th: ({ children }) => <th className="text-left px-4 py-3 font-bold">{children}</th>,
  td: ({ children }) => <td className="px-4 py-3 text-gray-800 align-top break-words">{children}</td>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-teal-700 font-semibold underline underline-offset-2 break-all">{children}</a>,
};

const INLINE_MD = { ...MD_COMPONENTS, p: ({ children }) => <span>{children}</span> };

const MarkdownBody = ({ md }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{md}</ReactMarkdown>
);
const InlineMD = ({ md }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]} components={INLINE_MD}>{md}</ReactMarkdown>
);

/* ── Deterministic parsers (all callers fall back to MarkdownBody) ── */
const parseListItems = (md) => {
  const items = [];
  let cur = null;
  md.split('\n').forEach((line) => {
    const m = line.match(/^\s*(?:\d+\.|-|\*)\s+(.*)$/);
    if (m) { if (cur) items.push(cur); cur = m[1]; }
    else if (cur && line.trim()) cur += ' ' + line.trim();
  });
  if (cur) items.push(cur);
  return items;
};

const parseSnapshotTable = (md) => {
  const rows = [];
  const rest = [];
  md.split('\n').forEach((line) => {
    const t = line.trim();
    if (t.startsWith('|')) {
      const cells = t.split('|').slice(1, -1).map((c) => c.trim().replace(/\*\*/g, ''));
      if (cells.length >= 2 && !/^[-:\s]+$/.test(cells[0]) && !/^signal$/i.test(cells[0])) {
        rows.push({ signal: cells[0], rating: cells[1], why: cells[2] || '' });
      }
    } else if (t) rest.push(line);
  });
  return { rows, note: rest.join('\n').trim() };
};

/* "**Label:** text" blocks separated by blank lines → [{label, text}] */
const parseBoldBlocks = (md) => {
  const blocks = [];
  const loose = [];
  md.split(/\n\s*\n/).forEach((raw) => {
    const b = raw.trim();
    if (!b) return;
    const m = b.match(/^\*\*([^*]+?)\*\*\s*:?\s*([\s\S]*)$/);
    if (m) blocks.push({ label: m[1].replace(/[:—–-]\s*$/, '').trim(), text: m[2].trim() });
    else loose.push(b);
  });
  return { blocks, loose };
};

/* ── Quiz — tap option → instant reveal + explanation ── */
const Quiz = ({ quiz }) => {
  const flat = useMemo(() => {
    if (!Array.isArray(quiz?.questions)) return [];
    return quiz.questions.map((q, i) => ({ ...q, id: q.id ?? `q${i}` }));
  }, [quiz]);
  const [responses, setResponses] = useState({});
  if (!quiz || !flat.length) return null;

  const score = flat.reduce((s, q) => (responses[q.id] !== undefined && responses[q.id] === q.answerIndex ? s + 1 : s), 0);
  const answered = Object.keys(responses).length;
  const isDone = answered === flat.length;
  const passScore = quiz.passScore || Math.ceil(flat.length * 0.7);
  const passed = score >= passScore;
  const resultLabel = passed
    ? `You cleared the ${passScore}/${flat.length} pass mark.`
    : `Pass mark is ${passScore}/${flat.length}. Reset and try again after revisiting the sections above.`;

  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 text-white p-4 md:p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-[12.5px] uppercase font-bold tracking-widest text-white/80">Score</p>
            <p className="text-2xl font-extrabold" data-testid="quiz-score">
              {score} <span className="text-base font-medium text-white/80">/ {flat.length}</span>
            </p>
          </div>
          <div className="flex-1 mx-0 sm:mx-6">
            <div className="h-2 w-full rounded-full bg-white/25 overflow-hidden">
              <div className="h-full bg-white rounded-full transition-all" style={{ width: `${(answered / flat.length) * 100}%` }} />
            </div>
            <p className="text-[13px] text-white/80 mt-1">{answered} of {flat.length} answered · pass at {passScore}/{flat.length}</p>
          </div>
          <button type="button" onClick={() => setResponses({})} data-testid="quiz-reset"
            className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-white/90 hover:text-white">
            <RotateCcw size={14} /> Reset
          </button>
        </div>
      </div>

      {flat.map((q, idx) => {
        const chosen = responses[q.id];
        const isSubmitted = chosen !== undefined;
        const correct = q.answerIndex;
        return (
          <div key={q.id} className="bg-white rounded-2xl border border-gray-200 p-5 md:p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-2 flex-wrap"><Chip tone="slate">Q{idx + 1}</Chip></div>
            <p className="font-semibold text-gray-900 mb-4 text-[17px] md:text-base leading-snug break-words [overflow-wrap:anywhere]"><InlineMD md={q.question} /></p>
            <div className="space-y-2">
              {(q.options || []).map((opt, oi) => {
                const isChosen = chosen === oi;
                const isCorrect = isSubmitted && oi === correct;
                const isWrong = isSubmitted && isChosen && oi !== correct;
                return (
                  <button key={oi} type="button" data-testid={`quiz-q${idx}-opt${oi}`} disabled={isSubmitted}
                    onClick={() => setResponses((p) => ({ ...p, [q.id]: oi }))}
                    className={`w-full text-left px-4 py-3 rounded-xl border-2 text-[15px] md:text-[17px] transition-all flex items-start gap-3 ${
                      isCorrect ? 'border-emerald-400 bg-emerald-50 text-emerald-900'
                        : isWrong ? 'border-rose-400 bg-rose-50 text-rose-900'
                        : 'border-gray-200 hover:border-violet-300 bg-white text-gray-800'
                    } ${isSubmitted ? 'cursor-default' : 'cursor-pointer'}`}>
                    <span className="mt-0.5">
                      {isSubmitted
                        ? (oi === correct
                            ? <CheckCircle2 size={18} className="text-emerald-600" />
                            : (isChosen ? <XCircle size={18} className="text-rose-600" /> : <span className="w-[18px] h-[18px] inline-block rounded-full border border-gray-300" />))
                        : <span className="w-[18px] h-[18px] inline-block rounded-full border border-gray-300" />}
                    </span>
                    <span className="flex-1 min-w-0 break-words [overflow-wrap:anywhere]"><InlineMD md={opt} /></span>
                  </button>
                );
              })}
            </div>
            {isSubmitted && (
              <div className="mt-4 rounded-xl bg-sky-50 border border-sky-200 p-4">
                <p className="text-[13px] font-bold uppercase tracking-widest text-sky-700 mb-1">Explanation</p>
                <p className="text-[15px] text-sky-900 leading-relaxed break-words [overflow-wrap:anywhere]"><InlineMD md={q.explanation} /></p>
              </div>
            )}
          </div>
        );
      })}

      {isDone && (
        <div className={`rounded-3xl border-2 p-6 text-center shadow-sm ${passed ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}>
          <Trophy className={`mx-auto mb-2 ${passed ? 'text-emerald-700' : 'text-amber-700'}`} size={28} />
          <p className={`text-[13px] uppercase font-bold tracking-widest mb-1 ${passed ? 'text-emerald-700' : 'text-amber-700'}`}>Final Result</p>
          <p className={`text-xl md:text-2xl font-extrabold mb-1 ${passed ? 'text-emerald-900' : 'text-amber-900'}`} data-testid="quiz-result">{score} / {flat.length}</p>
          <p className={`text-[15px] md:text-base max-w-xl mx-auto leading-relaxed ${passed ? 'text-emerald-800' : 'text-amber-800'}`}>{resultLabel}</p>
        </div>
      )}
    </div>
  );
};

/* ── Per-section bodies ── */

/* 1 · Key Takeaways — numbered rows with accent left border */
const TldrBody = ({ md }) => {
  const items = parseListItems(md);
  if (!items.length) return <MarkdownBody md={md} />;
  return (
    <div className="space-y-3">
      {items.map((t, i) => (
        <div key={i} className="rounded-2xl bg-amber-50/60 border border-amber-200 border-l-4 border-l-amber-400 p-4 flex items-start gap-3">
          <span className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white font-extrabold text-[15px] flex items-center justify-center shrink-0">{i + 1}</span>
          <p className="text-[17px] text-gray-800 leading-relaxed break-words [overflow-wrap:anywhere] min-w-0"><InlineMD md={t} /></p>
        </div>
      ))}
    </div>
  );
};

/* 2 · Snapshot — rating bars + "How to raise this" callout */
const SnapshotBody = ({ md }) => {
  const { rows, note } = parseSnapshotTable(md);
  if (!rows.length) return <MarkdownBody md={md} />;
  const grads = [
    'from-indigo-400 to-violet-500', 'from-teal-400 to-emerald-500',
    'from-amber-400 to-orange-500', 'from-rose-400 to-pink-500',
    'from-sky-400 to-blue-500', 'from-purple-400 to-fuchsia-500',
  ];
  return (
    <>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5 mb-4">
        {rows.map((s, i) => (
          <div key={i} className="min-w-0">
            <Meter label={s.signal} valueRaw={s.rating} grad={grads[i % grads.length]} />
            {s.why && <p className="text-[13px] text-gray-500 mt-2 leading-relaxed break-words [overflow-wrap:anywhere]">{s.why}</p>}
          </div>
        ))}
      </div>
      {note && (
        <Panel label="How to raise this" tone="cream">
          <div className="text-[15px] [&_p]:text-[15px] [&_p]:my-1">
            <MarkdownBody md={note.replace(/^\*|\*$/g, '').replace(/^_+|_+$/g, '').replace(/^\**\s*How to raise this:?\s*\**\s*/i, '')} />
          </div>
        </Panel>
      )}
    </>
  );
};

/* Related roles — clickable pills linking to sibling role previews */
const RelatedRolesLine = ({ text }) => {
  const location = useLocation();
  const items = [];
  const re = /#(\d+)\s+([^,#]+)/g;
  let m;
  while ((m = re.exec(text))) items.push({ n: m[1], name: m[2].trim().replace(/[.,\s]+$/, '') });
  if (!items.length) return null;
  const base = location.pathname.replace(/\/\d+\/?$/, '/');
  return (
    <div className="pt-1 flex flex-wrap items-center gap-2" data-testid="related-roles">
      <span className="text-[12.5px] uppercase font-bold tracking-[0.18em] text-purple-800">Related roles</span>
      {items.map((it) => (
        <Link
          key={it.n}
          to={`${base}${it.n}`}
          data-testid={`related-role-link-${it.n}`}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-purple-200 bg-purple-50 text-purple-800 text-[13px] font-semibold hover:bg-purple-100 transition-colors max-w-full break-words [overflow-wrap:anywhere]"
        >
          #{it.n} {it.name} <ChevronRight size={12} />
        </Link>
      ))}
    </div>
  );
};

/* 4 · The Role — 3 stacked level panels (blue / purple / green) + related links */
const WhatBody = ({ md }) => {
  const { blocks, loose } = parseBoldBlocks(md);
  if (blocks.length < 2) return <MarkdownBody md={md} />;
  const related = blocks.find((b) => /^related roles/i.test(b.label));
  const levels = blocks.filter((b) => b !== related);
  const tones = ['blue', 'violet', 'mint'];
  return (
    <div className="space-y-3">
      {levels.map((b, i) => (
        <Panel key={i} label={b.label} tone={tones[i % tones.length]}>
          <InlineMD md={b.text} />
        </Panel>
      ))}
      {loose.map((t, i) => <p key={`l${i}`} className="text-[17px] text-gray-800 leading-relaxed"><InlineMD md={t} /></p>)}
      {related && <RelatedRolesLine text={related.text} />}
    </div>
  );
};

/* 5 · Eligibility — first paragraph as amber reality-check panel */
const EligibilityBody = ({ md }) => {
  const parts = md.split(/\n\s*\n/);
  if (parts.length < 2) {
    return <Panel label="Reality check" tone="cream"><MarkdownBody md={md} /></Panel>;
  }
  return (
    <div className="space-y-3">
      <Panel label="Reality check" tone="cream"><InlineMD md={parts[0]} /></Panel>
      <MarkdownBody md={parts.slice(1).join('\n\n')} />
    </div>
  );
};

/* Two-column pipe table ("AI agent handles | You own") → paired tinted cells */
const parsePipeTable = (md) => {
  const lines = md.split('\n').map((l) => l.trim()).filter((l) => l.startsWith('|'));
  const cells = lines.map((l) => l.split('|').slice(1, -1).map((c) => c.trim()));
  if (cells.length < 2) return null;
  const header = cells[0].map((c) => c.replace(/\*\*/g, ''));
  const rows = cells.slice(1).filter((r) => !/^[-:\s]*$/.test(r.join('')));
  return { header, rows };
};
const AIvsYouTable = ({ md }) => {
  const t = parsePipeTable(md);
  if (!t || t.header.length !== 2) return <MarkdownBody md={md} />;
  return (
    <div className="space-y-2" data-testid="ai-vs-you-table">
      {t.rows.map((r, i) => (
        <div key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="rounded-xl border bg-sky-50 border-sky-200 p-3 min-w-0">
            <p className="text-[12px] uppercase font-bold tracking-widest text-sky-800 mb-1">{t.header[0]}</p>
            <p className="text-[15px] text-gray-800 leading-relaxed break-words [overflow-wrap:anywhere]">{(r[0] || '').replace(/\*\*/g, '')}</p>
          </div>
          <div className="rounded-xl border bg-emerald-50 border-emerald-200 p-3 min-w-0">
            <p className="text-[12px] uppercase font-bold tracking-widest text-emerald-800 mb-1">{t.header[1]}</p>
            <p className="text-[15px] text-gray-800 leading-relaxed break-words [overflow-wrap:anywhere]">{(r[1] || '').replace(/\*\*/g, '')}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

/* 6 · You + AI — Observed change (green) vs Informed projection (purple),
   rendered in document order; embedded pipe tables become paired cells */
const OperatingBody = ({ md }) => {
  const parts = md.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  const hasBold = parts.some((p) => /^\*\*[^*]+\*\*/.test(p));
  if (!hasBold) return <MarkdownBody md={md} />;
  return (
    <div className="space-y-3">
      {parts.map((p, i) => {
        if (p.startsWith('|')) return <AIvsYouTable key={i} md={p} />;
        const m = p.match(/^\*\*([^*]+?)\*\*\s*:?\s*([\s\S]*)$/);
        if (m) {
          const label = m[1].replace(/[:—–-]\s*$/, '').trim();
          const tone = /observed/i.test(label) ? 'mint' : /projection/i.test(label) ? 'violet' : 'sky';
          const lbl = /projection/i.test(label) ? `${label} — projection, not a guarantee` : label;
          return <Panel key={i} label={lbl} tone={tone}><MarkdownBody md={m[2]} /></Panel>;
        }
        return <p key={i} className="text-[17px] text-gray-800 leading-relaxed break-words [overflow-wrap:anywhere]"><InlineMD md={p} /></p>;
      })}
    </div>
  );
};

/* 7 · Real Company — blue "how integration happens" panel */
const IntegrationBody = ({ md }) => (
  <Panel label="How integration happens" tone="sky"><MarkdownBody md={md} /></Panel>
);

/* 8 · Risk Map — 5 tinted panels */
const RISK_TONES = [
  { match: /already automatable/i,  tone: 'cream' },
  { match: /agent-assisted/i,       tone: 'sky' },
  { match: /human-owned/i,          tone: 'mint' },
  { match: /ai-created risk/i,      tone: 'rose' },
  { match: /defence|defense/i,      tone: 'violet' },
];
const RiskMapBody = ({ md }) => {
  const { blocks, loose } = parseBoldBlocks(md);
  if (blocks.length < 2) return <MarkdownBody md={md} />;
  return (
    <div className="space-y-3">
      {blocks.map((b, i) => {
        const tone = (RISK_TONES.find((t) => t.match.test(b.label)) || { tone: 'sky' }).tone;
        return <Panel key={i} label={b.label} tone={tone}><InlineMD md={b.text} /></Panel>;
      })}
      {loose.map((t, i) => <p key={`l${i}`} className="text-[17px] text-gray-800 leading-relaxed"><InlineMD md={t} /></p>)}
    </div>
  );
};

/* 9 · Roadmap — 3 lane cards with labelled rows */
const parseLaneRows = (body) => {
  const b = body
    .replace(/(?<=[.?!])\s+(?=(?:\*\*)?[A-Z][A-Za-z /–—-]{2,45}:)/g, '\n\n')
    .replace(/\n(?=(?:\*\*)?[A-Z][A-Za-z /–—-]{2,45}:)/g, '\n\n');
  const rows = [];
  const loose = [];
  b.split(/\n\s*\n/).forEach((raw) => {
    const t = raw.trim();
    if (!t) return;
    const m = t.match(/^(?:\*\*)?([A-Z][^:*]{2,45})(?:\*\*)?:\s*(?:\*\*)?\s*([\s\S]*)$/);
    if (m) rows.push({ label: m[1].trim(), text: m[2].replace(/^\*\*/, '').trim() });
    else loose.push(t);
  });
  return { rows, loose };
};
const RoadmapBody = ({ md }) => {
  const laneParts = md.split(/(?:^|\n)###\s+/).filter((p) => p.trim());
  const head = md.trim().startsWith('###') ? '' : (laneParts.shift() || '').trim();
  const lanes = laneParts.map((p) => {
    const nl = p.indexOf('\n');
    const title = (nl === -1 ? p : p.slice(0, nl)).trim();
    const body = nl === -1 ? '' : p.slice(nl + 1);
    const { rows, loose } = parseLaneRows(body);
    return { title, rows, loose };
  });
  if (!lanes.length) return <MarkdownBody md={md} />;
  return (
    <div className="space-y-4">
      {head && <p className="text-[17px] text-gray-800 leading-relaxed"><InlineMD md={head} /></p>}
      <div className="grid lg:grid-cols-3 gap-3">
        {lanes.map((lane, i) => (
          <div key={i} className="rounded-2xl border-2 border-gray-200 bg-white p-4 md:p-5 min-w-0">
            <div className="flex items-center gap-2.5 mb-3">
              <span className={`w-8 h-8 rounded-full bg-gradient-to-br ${['from-blue-400 to-indigo-500', 'from-emerald-400 to-teal-500', 'from-purple-400 to-fuchsia-500'][i % 3]} text-white font-extrabold text-[15px] flex items-center justify-center shrink-0`}>{i + 1}</span>
              <p className="font-extrabold text-gray-900 leading-snug">{stripEmoji(lane.title)}</p>
            </div>
            <div className="space-y-2.5">
              {lane.rows.map((b, j) => (
                <div key={j} className={`rounded-xl border p-3 ${/bottleneck/i.test(b.label) ? 'bg-amber-50 border-amber-200' : /action/i.test(b.label) ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
                  <p className={`text-[12px] uppercase font-bold tracking-widest mb-1 ${/bottleneck/i.test(b.label) ? 'text-amber-800' : /action/i.test(b.label) ? 'text-emerald-800' : 'text-gray-500'}`}>{b.label}</p>
                  <p className="text-[15px] text-gray-800 leading-relaxed break-words [overflow-wrap:anywhere]"><InlineMD md={b.text} /></p>
                </div>
              ))}
              {lane.loose.map((t, j) => <p key={`l${j}`} className="text-[15px] text-gray-800 leading-relaxed"><InlineMD md={t} /></p>)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* 10 · Skill Stack — colored layer groups; "chips — description" becomes
   pill chips with the explanation text below */
const SKILL_TONES = ['teal', 'mint', 'sky', 'violet', 'cream', 'blue'];
const SkillsBody = ({ md }) => {
  const { blocks, loose } = parseBoldBlocks(md);
  if (blocks.length < 2) return <MarkdownBody md={md} />;
  return (
    <div className="space-y-3">
      {blocks.map((b, i) => {
        const di = b.text.indexOf(' — ');
        const chips = di > -1 ? b.text.slice(0, di).split(/,\s+/).map((c) => c.trim()).filter(Boolean) : [];
        const rest = di > -1 ? b.text.slice(di + 3).trim() : b.text;
        return (
          <Panel key={i} label={b.label} tone={SKILL_TONES[i % SKILL_TONES.length]}>
            {chips.length ? (
              <>
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`skill-chips-${i}`}>
                  {chips.map((c, j) => <Chip key={j}>{c.replace(/\*\*/g, '')}</Chip>)}
                </div>
                <p className="text-[15px] text-gray-700 leading-relaxed break-words [overflow-wrap:anywhere]"><InlineMD md={rest} /></p>
              </>
            ) : (
              <InlineMD md={b.text} />
            )}
          </Panel>
        );
      })}
      {loose.map((t, i) => <p key={`l${i}`} className="text-[17px] text-gray-800 leading-relaxed"><InlineMD md={t} /></p>)}
    </div>
  );
};

/* 11 · Learning Path — vertical stepper with numbered nodes + Gate pill */
const LearningBody = ({ md }) => {
  const items = parseListItems(md);
  if (items.length < 3) return <MarkdownBody md={md} />;
  return (
    <div className="space-y-3">
      {items.map((raw, i) => {
        const gateM = raw.match(/\*Gate:\*\s*([\s\S]*)$/i);
        const gate = gateM ? gateM[1].trim() : null;
        let body = gateM ? raw.slice(0, gateM.index).trim() : raw;
        const titleM = body.match(/^\*\*([^*]+?)\*\*\s*/);
        const title = titleM ? titleM[1].replace(/[.\s]+$/, '') : null;
        if (titleM) body = body.slice(titleM[0].length).trim();
        return (
          <div key={i} className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5">
            <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-5">
              <div className="flex items-center gap-3 md:flex-col md:items-center md:w-16 shrink-0">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white font-extrabold text-[15px] flex items-center justify-center">{i + 1}</div>
              </div>
              <div className="flex-1 min-w-0">
                {title && <p className="font-bold text-gray-900 mb-1.5 leading-snug">{title}</p>}
                <p className="text-[17px] text-gray-800 leading-relaxed break-words [overflow-wrap:anywhere]"><InlineMD md={body} /></p>
                {gate && (
                  <div className="mt-2.5 flex items-start gap-2">
                    <span className="shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200 text-[12.5px] font-bold uppercase tracking-wider mt-0.5">Gate</span>
                    <p className="text-[15px] text-gray-600 leading-relaxed break-words [overflow-wrap:anywhere]"><InlineMD md={gate} /></p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* 12 · Build Proof — Starter / Stronger / Standout tinted rows */
const ProofBody = ({ md }) => {
  const norm = md
    .replace(/\n-\s+\*\*/g, '\n\n**')
    .replace(/\n(?=\*\*[^*\n]+:\*\*)/g, '\n\n');
  const { blocks, loose } = parseBoldBlocks(norm);
  if (!blocks.length) return <Panel label="Proof project" tone="cream"><MarkdownBody md={md} /></Panel>;
  const tones = ['mint', 'sky', 'violet'];
  return (
    <div className="space-y-3">
      {blocks.map((b, i) => (
        <Panel key={i} label={b.label} tone={tones[i % tones.length]}><InlineMD md={b.text} /></Panel>
      ))}
      {loose.map((t, i) => (
        <Panel key={`l${i}`} label={null} tone="cream"><InlineMD md={t} /></Panel>
      ))}
    </div>
  );
};

/* 13 · Getting Hired — levelled sub-blocks (Final-year / Fresher / Working) */
const HIRE_TONES = ['sky', 'mint', 'violet'];
const HiringBody = ({ md }) => {
  const { blocks, loose } = parseBoldBlocks(md);
  if (blocks.length >= 2) {
    return (
      <div className="space-y-3">
        {blocks.map((b, i) => (
          <Panel key={i} label={b.label} tone={HIRE_TONES[i % HIRE_TONES.length]}>
            <InlineMD md={b.text} />
          </Panel>
        ))}
        {loose.map((t, i) => <MarkdownBody key={`l${i}`} md={t} />)}
      </div>
    );
  }
  const parts = md.split(/\n\s*\n/);
  return (
    <div className="space-y-3">
      <Panel label="Market reality" tone="sky"><InlineMD md={parts[0]} /></Panel>
      {parts.length > 1 && <MarkdownBody md={parts.slice(1).join('\n\n')} />}
    </div>
  );
};

/* 14 · Myths — myth (red) vs reality (green) rows */
const MythBody = ({ md }) => {
  const pairs = [];
  const chunks = [];
  md.split('\n').forEach((line) => {
    line.split(/\s-\s+(?=[*_“"])/).forEach((c) => chunks.push(c));
  });
  chunks.forEach((c) => {
    const t = c.replace(/^\s*[-*]\s+/, '').trim();
    if (!t) return;
    const m = t.match(/^(?:\*\*\s*)?Myth:?(?:\s*\*\*)?\s*([\s\S]*?)(?:\*\*\s*)?\bReality:?(?:\s*\*\*)?\s*([\s\S]+)$/i);
    if (m) { pairs.push({ myth: m[1].trim(), reality: m[2].trim() }); return; }
    const m2 = t.match(/^([\s\S]*?)\bReality:?\s*([\s\S]+)$/i);
    if (m2 && m2[1].trim()) {
      pairs.push({ myth: m2[1].replace(/(?:→|->|\s)+$/, '').trim(), reality: m2[2].trim() });
      return;
    }
    const arr = t.split(/→|->/);
    if (arr.length >= 2 && arr[0].trim()) {
      pairs.push({ myth: arr[0].trim(), reality: arr.slice(1).join('→').trim() });
    }
  });
  if (!pairs.length) return <MarkdownBody md={md} />;
  return (
    <div className="space-y-3">
      {pairs.map((p, i) => (
        <div key={i} className="grid md:grid-cols-2 gap-3">
          <Panel label="Myth" tone="rose"><InlineMD md={p.myth} /></Panel>
          <Panel label="Reality" tone="mint"><InlineMD md={p.reality} /></Panel>
        </div>
      ))}
    </div>
  );
};

/* 15 · Prompt Pack — levelled sub-blocks (Final-year / Fresher / Working),
   each prompt is a copyable code-style card */
const PromptsBody = ({ md }) => {
  const [copied, setCopied] = useState(null);
  const levels = useMemo(() => {
    const { blocks } = parseBoldBlocks(md);
    return blocks.map((b) => ({
      label: b.label,
      prompts: b.text
        .split('\n')
        .map((l) => l.trim().replace(/^[“”"\s]+|[“”"\s]+$/g, ''))
        .filter(Boolean),
    })).filter((b) => b.prompts.length);
  }, [md]);
  const flatItems = useMemo(() => {
    if (levels.length) return [];
    return md
      .split(/”\s*(?:-\s*)?\n?\s*[-–]?\s*“|\n\s*(?:[-*]|\d+\.)\s+/)
      .map((p) => p.trim().replace(/^[“”"\s]+|[“”"\s]+$/g, ''))
      .filter(Boolean);
  }, [md, levels]);
  const copy = (text, key) => {
    const clean = text.replace(/^[“"]|[”"]$/g, '');
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(clean);
    setCopied(key);
    setTimeout(() => setCopied(null), 1600);
  };
  const PromptCard = ({ t, k, testid }) => (
    <div className="rounded-2xl border border-cyan-200 bg-cyan-50/50 p-4 flex items-start gap-3">
      <p className="flex-1 min-w-0 text-[15px] text-gray-800 leading-relaxed font-mono break-words [overflow-wrap:anywhere]">{t}</p>
      <button
        type="button"
        data-testid={testid}
        onClick={() => copy(t, k)}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-cyan-300 bg-white text-cyan-800 text-[13px] font-bold hover:bg-cyan-100 transition-colors"
      >
        {copied === k ? <Check size={13} /> : <Copy size={13} />} {copied === k ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
  if (levels.length) {
    return (
      <div className="space-y-5">
        {levels.map((lv, bi) => (
          <div key={bi} data-testid={`prompt-level-${bi}`}>
            <p className="text-[12.5px] uppercase font-bold tracking-[0.18em] text-cyan-800 mb-2">{lv.label}</p>
            <div className="space-y-2.5">
              {lv.prompts.map((t, i) => (
                <PromptCard key={i} t={t} k={`${bi}-${i}`} testid={`prompt-copy-${bi}-${i}`} />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (!flatItems.length) return <MarkdownBody md={md} />;
  return (
    <div className="space-y-3">
      {flatItems.map((t, i) => (
        <PromptCard key={i} t={t} k={i} testid={`prompt-copy-${i}`} />
      ))}
    </div>
  );
};

/* 16 · One Idea — centered gradient banner */
const OneIdeaBody = ({ md }) => (
  <div className="rounded-2xl bg-gradient-to-br from-teal-500 via-emerald-500 to-cyan-600 text-white p-6 md:p-8 text-center shadow-sm">
    <Lightbulb size={26} className="mx-auto mb-3 text-amber-300" />
    <p className="text-base md:text-xl font-bold leading-relaxed [&_strong]:font-extrabold">
      <InlineMD md={md.replace(/\*\*/g, '')} />
    </p>
  </div>
);

const SECTION_BODIES = {
  tldr: TldrBody,
  snapshot: SnapshotBody,
  what: WhatBody,
  eligibility: EligibilityBody,
  operating: OperatingBody,
  integration: IntegrationBody,
  'risk-map': RiskMapBody,
  roadmap: RoadmapBody,
  skills: SkillsBody,
  learning: LearningBody,
  proof: ProofBody,
  hiring: HiringBody,
  myth: MythBody,
  prompts: PromptsBody,
  'one-idea': OneIdeaBody,
};

/* ── Main component — always-open Section cards, AI Pathway layout ── */
const RolePreview = ({ role, fullNarrative = false, renderBody = {}, afterSection }) => {
  if (!role) return null;
  const sections = Array.isArray(role.sections_md) ? role.sections_md : [];
  const quiz = role.quiz;

  const renderSection = (s) => {
    if (s.kind === 'layer') {
      return (
        <div key={s.id} id={s.id} className="scroll-mt-52 space-y-3">
          <RoadmapLayerEntry
            itemId={role.catalog_number}
            layerNo={s.layer_no}
            heading={s.heading}
          />
          {afterSection?.(s)}
        </div>
      );
    }
    const nav = PREVIEW_SECTIONS.find((c) => c.id === s.id);
    const Body = fullNarrative ? null : SECTION_BODIES[s.id];
    const defaultBody = Body ? <Body md={s.md} /> : <MarkdownBody md={s.md} />;
    return (
      <Section
        key={s.id}
        id={s.id}
        icon={nav?.icon || Layers}
        title={stripEmoji(s.heading)}
        accent={SECTION_ACCENT[s.id] || 'teal'}
      >
        {renderBody[s.id] ? renderBody[s.id](s, defaultBody) : defaultBody}
        {afterSection?.(s)}
      </Section>
    );
  };

  return (
    <div className="space-y-6">
      {sections.map(renderSection)}

      {quiz && (
        <Section id="quiz" icon={Brain} title={quiz.title || 'Self-Check Quiz'} accent="purple">
          <Quiz quiz={quiz} />
        </Section>
      )}
    </div>
  );
};

export default RolePreview;
